import { z } from "zod";

/** A sender identity from `config('mail.profiles')` an admin email can be sent from. */
export const AdminEmailFromProfileSchema = z.object({
    key: z.string(),
    address: z.string(),
    name: z.string(),
});

/**
 * One-way progression driven by Postmark webhooks; `bounced` is terminal.
 * Clicks and spam complaints are system tags instead, so neither masks these.
 */
export const AdminEmailStatusSchema = z.enum([
    "sent",
    "delivered",
    "opened",
    "bounced",
]);

export const AdminEmailSchema = z.object({
    id: z.number(),
    admin_id: z.number(),
    expert_id: z.number().nullable(),
    to_email: z.string(),
    from_profile: z.string(),
    from_email: z.string(),
    reply_to: z.string().nullable(),
    subject: z.string(),
    body: z.string(),
    /** Tag names, find-or-create by normalized name like expert tags. */
    tags: z.array(z.string()).default([]),
    status: AdminEmailStatusSchema,
    message_id: z.string().nullable(),
    postmark_message_id: z.string().nullable(),
    postmark_bounce_id: z.number().nullable(),
    created_at: z.string(),
    updated_at: z.string().nullable(),
});

export const SendAdminEmailRequestSchema = z.object({
    to: z.string(),
    /** Profile key from AdminEmailFromProfileSchema, e.g. `support`. */
    from: z.string(),
    subject: z.string().min(1).max(255),
    /** Richtext (HTML), rendered inside the standard V2 email layout. */
    body: z.string().min(1).max(65000),
    /**
     * Applied to the email itself. Postmark accepts one Tag per message, so
     * the first is forwarded as the message Tag.
     */
    tags: z.array(z.string().min(1).max(50)).max(25).optional(),
    /** Sets Reply-To to the support address. Default: no Reply-To. */
    reply_to_support: z.boolean().optional(),
    /** Required when expert_tags is present. */
    expert_id: z.number().optional(),
    /** Attached to the expert (find-or-create by name) only after the send succeeds. */
    expert_tags: z.array(z.string().max(50)).max(25).optional(),
}).refine((body) => body.expert_tags === undefined || body.expert_id !== undefined, {
    // Mirrors the API's own rule: tagging needs an expert to tag (422 otherwise).
    path: ["expert_id"],
    error: "expert_id is required when expert_tags is present",
});

export const AdminEmailListMetaSchema = z.object({
    total: z.number().int().nonnegative(),
});

/** Tags the webhook attaches itself; they carry their own meaning in the UI. */
export const ADMIN_EMAIL_SYSTEM_TAGS = ["clicked", "spam-complaint"] as const;

/** An entry of the email tag catalog, mirroring the expert tag catalog. */
export const AdminEmailTagSchema = z.object({
    id: z.number(),
    name: z.string(),
    emails_count: z.number().int().nonnegative(),
});

export type AdminEmailFromProfile = z.infer<typeof AdminEmailFromProfileSchema>;
export type AdminEmailListMeta = z.infer<typeof AdminEmailListMetaSchema>;
export type AdminEmailTag = z.infer<typeof AdminEmailTagSchema>;
export type AdminEmailStatus = z.infer<typeof AdminEmailStatusSchema>;
export type AdminEmail = z.infer<typeof AdminEmailSchema>;
export type SendAdminEmailRequest = z.infer<typeof SendAdminEmailRequestSchema>;
